import React, { useEffect, useState } from 'react'
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, Loader2Icon, PlusIcon } from 'lucide-react'
import {
  completeInboxTask,
  createInboxTask,
  fetchInboxTasks,
  isTaskOverdue,
  sortTasksForQueue,
} from '../../api/inboxTasks'
import { TASK_QUICK_PRESETS } from '../../lib/inboxFilters'
import { getErrorMessage } from '../../lib/errors'
import type { CreateInboxTaskInput, InboxFeedItem, InboxTask } from '../../types/inbox'
import { canCompleteInboxTask } from '../../lib/inboxPermissions'

interface InboxTasksPanelProps {
  sourceItem: InboxFeedItem | null
  currentUserId: string | null
  currentUserRole: string | null
  assigneeDefaultId: string | null
  onTasksChanged?: () => void
  defaultCollapsed?: boolean
  embedded?: boolean
}

export function InboxTasksPanel({
  sourceItem,
  currentUserId,
  currentUserRole,
  assigneeDefaultId,
  onTasksChanged,
  defaultCollapsed = true,
  embedded = false,
}: InboxTasksPanelProps) {
  const [tasks, setTasks] = useState<InboxTask[]>([])
  const [loading, setLoading] = useState(false)
  const [title, setTitle] = useState('')
  const [dueAt, setDueAt] = useState('')
  const [saving, setSaving] = useState(false)
  const [collapsed, setCollapsed] = useState(embedded ? false : defaultCollapsed)

  useEffect(() => {
    if (!sourceItem) {
      setTasks([])
      return
    }
    setLoading(true)
    fetchInboxTasks({
      sourceType: sourceItem.itemType,
      sourceId: sourceItem.id,
    })
      .then((rows) => setTasks(sortTasksForQueue(rows.filter((t) => t.status !== 'done'))))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false))
  }, [sourceItem?.itemType, sourceItem?.id])

  if (!sourceItem) return null

  const effectiveAssigneeId = assigneeDefaultId || currentUserId

  async function handleCreate(presetTitle?: string, actionType?: string) {
    if (!sourceItem || !effectiveAssigneeId) return
    const taskTitle = (presetTitle || title).trim()
    if (!taskTitle) return
    setSaving(true)
    try {
      const input: CreateInboxTaskInput = {
        title: taskTitle,
        assignee_id: effectiveAssigneeId,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        action_type: actionType || null,
        source_type: sourceItem.itemType,
        source_id: sourceItem.id,
        customer_id: sourceItem.customerId,
        property_id: sourceItem.propertyId,
        contract_id: sourceItem.contractId,
        job_id: sourceItem.jobId,
        lead_id: sourceItem.leadId,
        proposal_id: sourceItem.proposalId,
      }
      const created = await createInboxTask(input)
      setTasks((prev) => sortTasksForQueue([created, ...prev]))
      setTitle('')
      setDueAt('')
      onTasksChanged?.()
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to create task'))
    } finally {
      setSaving(false)
    }
  }

  async function markDone(task: InboxTask) {
    if (!canCompleteInboxTask(currentUserRole, currentUserId, task.assignee_id)) return
    try {
      const updated = await completeInboxTask(task.id)
      setTasks((prev) => prev.filter((t) => t.id !== updated.id))
      onTasksChanged?.()
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to complete task'))
    }
  }

  const taskList = (
    <>
      <div className="flex flex-wrap gap-1.5 mb-2 pt-2 w-full min-w-0">
        {TASK_QUICK_PRESETS.map((p) => (
          <button
            key={p.action_type}
            type="button"
            disabled={saving || !effectiveAssigneeId}
            onClick={() => handleCreate(p.title, p.action_type)}
            className="px-2 py-1 text-[11px] font-medium rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            {p.title}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2 mb-3 w-full min-w-0">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Task title…"
          className="w-full min-w-0 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex items-center gap-2 min-w-0 w-full">
          <input
            type="date"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="button"
            disabled={saving || !title.trim() || !effectiveAssigneeId}
            onClick={() => handleCreate()}
            className="inline-flex flex-shrink-0 items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <PlusIcon className="w-3.5 h-3.5" />
            Add
          </button>
        </div>
      </div>

      {tasks.length === 0 ? (
        <p className="text-xs text-gray-400">No open tasks linked to this item.</p>
      ) : (
        <ul className="space-y-2">
          {tasks.map((task) => (
            <li key={task.id} className="flex items-start gap-2 text-xs">
              <button
                type="button"
                disabled={!canCompleteInboxTask(currentUserRole, currentUserId, task.assignee_id)}
                onClick={() => markDone(task)}
                className="mt-0.5 p-1 rounded border border-gray-200 hover:bg-green-50 disabled:opacity-40"
                title="Mark done"
              >
                <CheckIcon className="w-3.5 h-3.5 text-green-600" />
              </button>
              <div className="flex-1 min-w-0">
                <p className={`font-medium truncate ${isTaskOverdue(task) ? 'text-red-600' : 'text-gray-900'}`}>
                  {task.title}
                </p>
                {task.due_at && (
                  <p className={`text-[11px] ${isTaskOverdue(task) ? 'text-red-500' : 'text-gray-500'}`}>
                    Due {new Date(task.due_at).toLocaleString()}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  )

  if (embedded) {
    return (
      <div className="flex flex-col flex-1 min-h-0 min-w-0 w-full overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 flex-shrink-0">
          {loading && <Loader2Icon className="w-3.5 h-3.5 animate-spin text-gray-400" />}
          {tasks.length > 0 && (
            <span className="ml-auto px-1.5 py-0.5 text-[10px] font-semibold bg-gray-100 text-gray-600 rounded-full">
              {tasks.length}
            </span>
          )}
        </div>
        <div className="flex-1 min-h-0 min-w-0 w-full overflow-y-auto overflow-x-hidden px-3 pb-3">{taskList}</div>
      </div>
    )
  }

  return (
    <div className="flex-shrink-0 border-t border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
      >
        {collapsed ? (
          <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Follow-up tasks</span>
        {tasks.length > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold bg-gray-100 text-gray-600 rounded-full">
            {tasks.length}
          </span>
        )}
        {loading && <Loader2Icon className="w-3.5 h-3.5 animate-spin text-gray-400 ml-auto" />}
      </button>
      {!collapsed && <div className="px-4 pb-3 border-t border-gray-100">{taskList}</div>}
    </div>
  )
}
